import { useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ApiPiece } from "@/hooks/use-projects";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useBrandPieces,
  useApprovePiece,
  useRejectPiece,
  useSubmitPiece,
  useRevisePiece,
  useUpdatePiece,
} from "@/hooks/use-pieces";
import { useUiStore } from "@/stores/ui-store";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { GalleryGrid } from "@/components/pieces/gallery-grid";
import { KanbanBoard } from "@/components/pieces/kanban-board";
import { PieceSlideOver } from "@/components/pieces/piece-slide-over";
import { PieceFilters as PieceFiltersBar } from "@/components/pieces/piece-filters";
import { RejectionModal } from "@/components/pieces/rejection-modal";
import { InlineEditor } from "@/components/pieces/inline-editor";
import { PostTypeEditor } from "@/components/composers/PostTypeEditor";
import type { PostType } from "@/hooks/use-post-generation";
import { useRegenerateMedia } from "@/hooks/use-pieces";
import { ShortcutHelp } from "@/components/ui/shortcut-help";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { Sparkles } from "lucide-react";

export function BrandReviewPage() {
  const { t } = useTranslation("compose");
  const { brand } = useBrandContext();
  const { canApprove, canEdit } = usePermissions();
  const { reviewViewMode, setReviewViewMode } = useUiStore();
  const navigate = useNavigate();

  // Filters — seed from ?status= so deep links (e.g. from Compose after
  // generation) land on the matching subset.
  const [searchParams] = useSearchParams();
  const initialStatus = (() => {
    const raw = searchParams.get("status");
    if (!raw) return "ALL";
    const v = raw.toUpperCase();
    return ["DRAFT", "PENDING_REVIEW", "APPROVED", "REJECTED", "SCHEDULED", "PUBLISHED", "FAILED"].includes(v)
      ? v
      : "ALL";
  })();
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [platformFilter, setPlatformFilter] = useState("ALL");
  const [postTypeFilter, setPostTypeFilter] = useState("ALL");

  // Data
  const { data, isLoading } = useBrandPieces(brand.id, {
    status: statusFilter === "ALL" ? undefined : statusFilter,
    platform: platformFilter === "ALL" ? undefined : platformFilter,
    postType: postTypeFilter === "ALL" ? undefined : postTypeFilter,
  });
  const pieces = data?.data ?? [];

  // Selection & slide-over
  const [selectedIdx, setSelectedIdx] = useState<number>(-1);
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const selectedPiece = selectedIdx >= 0 ? pieces[selectedIdx] ?? null : null;

  // Rejection modal
  const [rejectionTarget, setRejectionTarget] = useState<ApiPiece | null>(null);

  // Inline editing
  const [editingField, setEditingField] = useState<string | null>(null);

  // Mutations
  const approveMut = useApprovePiece(brand.id);
  const rejectMut = useRejectPiece(brand.id);
  const submitMut = useSubmitPiece(brand.id);
  const reviseMut = useRevisePiece(brand.id);
  const updateMut = useUpdatePiece(brand.id);
  const regenMediaMut = useRegenerateMedia(brand.id);

  // Handlers
  const handlePieceClick = useCallback(
    (piece: ApiPiece) => {
      const idx = pieces.findIndex((p) => p.id === piece.id);
      setSelectedIdx(idx);
      setSlideOverOpen(true);
      setEditingField(null);
    },
    [pieces],
  );

  const handleNavigate = useCallback(
    (dir: "prev" | "next") => {
      setSelectedIdx((prev) => {
        const next = dir === "next" ? prev + 1 : prev - 1;
        if (next < 0 || next >= pieces.length) return prev;
        return next;
      });
      setEditingField(null);
    },
    [pieces.length],
  );

  const handleApprove = useCallback(
    (pieceId: string) => approveMut.mutate(pieceId),
    [approveMut],
  );

  const handleRejectRequest = useCallback(
    (pieceId: string) => {
      const piece = pieces.find((p) => p.id === pieceId);
      if (piece) setRejectionTarget(piece);
    },
    [pieces],
  );

  const handleRejectConfirm = useCallback(
    (reason: string) => {
      if (rejectionTarget) {
        rejectMut.mutate(
          { pieceId: rejectionTarget.id, reason },
          { onSuccess: () => setRejectionTarget(null) },
        );
      }
    },
    [rejectionTarget, rejectMut],
  );

  const handleSubmit = useCallback(
    (pieceId: string) => submitMut.mutate(pieceId),
    [submitMut],
  );

  const handleRevise = useCallback(
    (pieceId: string) => reviseMut.mutate(pieceId),
    [reviseMut],
  );

  const handleSaveField = useCallback(
    (value: string | string[]) => {
      if (!selectedPiece || !editingField) return;
      const fields: Partial<Pick<ApiPiece, "copy" | "caption" | "hashtags">> = {};
      (fields as Record<string, string | string[]>)[editingField] = value;
      updateMut.mutate(
        { pieceId: selectedPiece.id, fields },
        { onSuccess: () => setEditingField(null) },
      );
    },
    [selectedPiece, editingField, updateMut],
  );

  // Keyboard shortcuts
  const shortcuts = useMemo(
    () => ({
      j: () => setSelectedIdx((prev) => Math.min(prev + 1, pieces.length - 1)),
      k: () => setSelectedIdx((prev) => Math.max(prev - 1, 0)),
      enter: () => {
        if (selectedIdx >= 0 && !slideOverOpen) setSlideOverOpen(true);
      },
      escape: () => {
        setSlideOverOpen(false);
        setEditingField(null);
      },
      arrowright: () => handleNavigate("next"),
      arrowleft: () => handleNavigate("prev"),
      a: () => {
        if (slideOverOpen && selectedPiece?.status === "PENDING_REVIEW" && canApprove) {
          handleApprove(selectedPiece.id);
        }
      },
      r: () => {
        if (slideOverOpen && selectedPiece?.status === "PENDING_REVIEW" && canApprove) {
          handleRejectRequest(selectedPiece.id);
        }
      },
      e: () => {
        if (slideOverOpen && canEdit && !editingField) {
          setEditingField("copy");
        }
      },
    }),
    [pieces.length, selectedIdx, slideOverOpen, selectedPiece, canApprove, canEdit, editingField, handleNavigate, handleApprove, handleRejectRequest],
  );

  useKeyboardShortcuts(shortcuts, true);

  // Edit content for slide-over. PostType-aware pieces get the full
  // PostTypeEditor (all fields at once). Legacy pieces fall through to the
  // single-field InlineEditor.
  const editContent =
    editingField && selectedPiece ? (
      selectedPiece.postType ? (
        <PostTypeEditor
          brandId={brand.id}
          postType={selectedPiece.postType as PostType}
          initial={{
            caption: selectedPiece.caption ?? "",
            title: selectedPiece.title ?? null,
            description: selectedPiece.description ?? null,
            hashtags: selectedPiece.hashtags ?? [],
            threadItems: (selectedPiece.threadItems as any) ?? null,
            platformKnobs: (selectedPiece.platformKnobs as any) ?? {},
            mediaUrls: selectedPiece.mediaUrls ?? [],
          }}
          onSave={async (changes) => {
            await new Promise<void>((resolve, reject) => {
              updateMut.mutate(
                { pieceId: selectedPiece.id, fields: changes as any },
                {
                  onSuccess: () => {
                    setEditingField(null);
                    resolve();
                  },
                  onError: (err) => reject(err),
                },
              );
            });
          }}
          onRegenerateMedia={() => regenMediaMut.mutateAsync(selectedPiece.id).then(() => undefined)}
          regenerateMediaPending={regenMediaMut.isPending}
          brandHandle={brand.slug}
          brandName={brand.name}
          disabled={updateMut.isPending}
        />
      ) : (
        <InlineEditor
          field={editingField as "copy" | "caption" | "hashtags"}
          value={
            editingField === "hashtags"
              ? selectedPiece.hashtags
              : (selectedPiece as unknown as Record<string, unknown>)[editingField] as string ?? ""
          }
          onSave={handleSaveField}
          onCancel={() => setEditingField(null)}
          isSaving={updateMut.isPending}
        />
      )
    ) : null;

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
      <h1 className="text-2xl font-bold">{t("review.title")}</h1>

      <PieceFiltersBar
        status={statusFilter}
        onStatusChange={setStatusFilter}
        platform={platformFilter}
        onPlatformChange={setPlatformFilter}
        postType={postTypeFilter}
        onPostTypeChange={setPostTypeFilter}
        viewMode={reviewViewMode}
        onViewModeChange={setReviewViewMode}
      />

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      ) : pieces.length === 0 ? (
        statusFilter === "ALL" && platformFilter === "ALL" && postTypeFilter === "ALL" ? (
          <EmptyState
            icon={Sparkles}
            title={t("review.emptyTitle")}
            description={t("review.emptyDescription")}
            action={{
              label: t("review.composeAction"),
              onClick: () => navigate(`/${brand.slug}/compose`),
            }}
          />
        ) : (
          <p className="py-16 text-center text-sm text-muted-foreground">
            {t("review.noPiecesFiltered")}
          </p>
        )
      ) : reviewViewMode === "gallery" ? (
        <GalleryGrid
          pieces={pieces}
          onPieceClick={handlePieceClick}
          onApprove={handleApprove}
          onReject={handleRejectRequest}
          selectedPieceId={selectedPiece?.id}
          canApprove={canApprove}
        />
      ) : (
        <KanbanBoard
          pieces={pieces}
          onPieceClick={handlePieceClick}
          onSubmit={handleSubmit}
          onApprove={handleApprove}
          onReject={handleRejectRequest}
          canApprove={canApprove}
        />
      )}

      <PieceSlideOver
        piece={selectedPiece}
        isOpen={slideOverOpen}
        onClose={() => { setSlideOverOpen(false); setEditingField(null); }}
        onNavigate={handleNavigate}
        onApprove={handleApprove}
        onReject={handleRejectRequest}
        onSubmit={handleSubmit}
        onRevise={handleRevise}
        canApprove={canApprove}
        canEdit={canEdit}
        editingField={editingField}
        onEditField={setEditingField}
        editContent={editContent}
      />

      <RejectionModal
        isOpen={!!rejectionTarget}
        onClose={() => setRejectionTarget(null)}
        onReject={handleRejectConfirm}
        shortCode={rejectionTarget?.shortCode ?? ""}
        isLoading={rejectMut.isPending}
      />

      <ShortcutHelp />
    </div>
  );
}
